import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import styles from "./Dashboard.module.scss";
import TreeMap from "../../components/charts/TreeMap";
import Card from "../../components/controls/Card";
import PieChartContainer from "../../components/charts/PieChart";
import LineChartContainer from "../../components/charts/LineChart";
import BarChartContainer from "../../components/charts/BarChart";
import Footer from "../../components/Footer/Footer";
import DashboardFilter from "../../components/Filter/DashboardFilter";
import ActivityCard from "../../components/ActivityCard";
import DashboardReportIcon from "../../assets/icons/dashboardReport.svg";
import FeedbackIcon from "../../assets/icons/feedback.svg";
import GlobeIcon from "../../assets/icons/globe.svg";
import UserIcon from "../../assets/icons/user.svg";
import DoneIcon from "../../assets/icons/doneIcon.svg";
import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { DashboardFilterType } from "../../interface";
import useGetDashboardData from "./useGetDashboardData.hook";
import {
  exportDashboardToExcel,
  exportDashboardToJSON,
  exportDashboardToPDF,
  getRangeDate,
  getSessionData,
  hasNonZeroData
} from "../../utils/helper";
import { DateRange } from "../../type/enums";
import { DateRangeLabels } from "../../utils/constants";
import { ToastHelper } from "../../utils/toastHelper";
import { useNavigate } from "react-router-dom";

import HorizontalBarChartContainer from "../../components/charts/HorizontalBarChart";
import { SYSTEM_DASHBOARD_URL } from "../../config";

const ExportDashboard = lazy(
  () => import("../../components/controls/Modals/ExportDashboard")
);

const Dashboard = () => {
  const userInfo = getSessionData();
  const navigate = useNavigate();
  const toastHelper = new ToastHelper();
  const [startDate, endDate] = getRangeDate("thisMonth" as DateRange);
  const [tempFilter, setTempFilter] = useState<DashboardFilterType>({
    dateRange: "thisMonth",
    reviewType: ["PV", "PCO", "PTQ"],
    region: [],
    country: [],
    affiliate: [],
    startDate,
    endDate,
    monthSelected: true,
  });

  const [filter, setFilter] = useState<DashboardFilterType>(tempFilter);

  const [exportModal, setExportModal] = useState<boolean>(false);

  const isReadOnly = userInfo?.role?.includes("read-only");

  const openExportModal = () => {
    setExportModal(true);
  };

  const closeExportModal = () => {
    setExportModal(false);
  };

  const handleDownload = async (type: "xlsx" | "pdf" | "json") => {
    try {
      switch (type) {
        case "xlsx":
          exportDashboardToExcel(dashboardData);
          break;
        case "json":
          exportDashboardToJSON(dashboardData);
          break;
        case "pdf":
          exportDashboardToPDF();
          break;
      }
      toastHelper.showToast({
        severity: "success",
        content: `Export successful: dashboard-export.${type}`,
      });
    } catch (err) {
      console.error(err);
      toastHelper.showToast({
        severity: "error",
        content:
          "Something went wrong while fetching data. Please try again later.",
      });
    }
  };

  const {
    data: dashboardData,
    isLoading,
    isError,
  } = useGetDashboardData(filter);

  const hasData = useMemo(() => {
    if (!dashboardData) return false;
    return hasNonZeroData(dashboardData);
  }, [dashboardData]);

  const applyHandler = () => {
    setFilter(tempFilter);
  };

  if (isError) {
    toastHelper.showToast({
      severity: "error",
      content:
        "Something went wrong while fetching data. Please try again later.",
    });
    navigate("/welcome");
  }

  const getReviewOutcomeTitle = useCallback(() => {
    let title = "";
    if (!isLoading && dashboardData?.reviewOutcome?.length > 0) {
      const countryWithMaxCount = dashboardData?.reviewOutcome?.reduce(
        (prev: any, current: any) =>
          prev && prev?.value > current?.value ? prev : current
      );
      title = `${
        countryWithMaxCount.name
      } has been outperforming ${DateRangeLabels[
        filter.dateRange as DateRange
      ].toLowerCase()}.`;
    }
    return title;
  }, [dashboardData?.reviewOutcome, isLoading, filter.dateRange]);

  const getReviewerEvaluationTitle = useCallback(
    (pastCount: number, currentCount: number) => {
      let change = 0;
      if (pastCount === 0) {
        change = currentCount === 0 ? 0 : 100;
      } else change = ((currentCount - pastCount) * 0.01) / pastCount;
      const isPositive = pastCount < currentCount;
      return `QC Fail Events have ${
        isPositive ? "risen" : "fallen"
      } by ${change}% this month.`;
    },
    []
  );

  const getActiveUserTitle = useCallback((past: any, current: any) => {
    const pastCount = past
      ? past?.reduce(
          (acc: any, item: any) =>
            acc +
            Number(item?.viewOnlyUsers) +
            Number(item?.responsibleUsers) +
            Number(item?.adminUsers) +
            Number(item?.reviewerUsers),
          0
        )
      : 0;
    const currentCount = current
      ? current?.reduce(
          (acc: any, item: any) =>
            acc +
            Number(item?.viewOnlyUsers) +
            Number(item?.responsibleUsers) +
            Number(item?.adminUsers) +
            Number(item?.reviewerUsers),

          0
        )
      : 0;
    let change = 0;
    if (pastCount === 0) {
      change = currentCount === 0 ? 0 : 100;
    } else change = ((currentCount - pastCount) * 0.01) / pastCount;
    const isPositive = pastCount < currentCount;

    return `Overall total records of Admin, Reviewer, Responsible & View only Users has ${
      isPositive ? "increased" : "decreased"
    } by ${change}% this month.`;
  }, []);

  const activityConfig = useMemo(
    () => [
      {
        id: 1,
        title: "Active User",
        description: "Total active users",
        count: dashboardData?.activityInfo?.activeUserCount,
        change: dashboardData?.activityInfo?.activeUserChange,
        icon: { src: UserIcon, alt: "user Icon" },
      },
      {
        id: 2,
        title: "Countries with Samples",
        description: `Out of ${dashboardData?.activityInfo?.sampleCountryTotalCount} registered`,
        count: dashboardData?.activityInfo?.sampleCountryCount,
        change: dashboardData?.activityInfo?.sampleCountryChange,
        icon: { src: GlobeIcon, alt: "globe Icon" },
      },
      {
        id: 3,
        title: "Total Reviews",
        description: "Across all categories",
        count: dashboardData?.activityInfo?.totalReviewCount,
        change: dashboardData?.activityInfo?.totalReviewChange,
        icon: { src: FeedbackIcon, alt: "feedback Icon" },
      },
      {
        id: 4,
        title: "Corrective Actions",
        description: "Active corrective actions",
        count: dashboardData?.activityInfo?.correctiveActionCount,
        change: dashboardData?.activityInfo?.correctiveActionChange,
        icon: { src: DoneIcon, alt: "done Icon" },
      },
    ],
    [dashboardData]
  );

  const handleSystemPerfDB = () => {
    window.open(`${SYSTEM_DASHBOARD_URL}`, "_blank");
  };

  const NoData = () => <Box className={styles.noData}>No Data to display</Box>;

  return (
    <Box className={styles.container}>
      <Container maxWidth="xl">
        <Typography className={styles.title}>
          Text Sentinel Performance Hub
        </Typography>
        <Typography className={styles.subtext}>
          Monitor key metrics and performance indicators
        </Typography>
        <DashboardFilter
          filter={tempFilter}
          onChange={setTempFilter}
          onApply={applyHandler}
        />
      </Container>
      <Divider className={styles.divider} />
      <Container maxWidth="xl" className={styles.contentSection}>
        <Box id="dashboardCanvas" p={2}>
          <Card>
            <Grid container spacing={0}>
              {activityConfig.map((activity) => (
                <Grid key={activity.id} item xs={12} sm={6} md={3}>
                  <ActivityCard
                    title={activity.title}
                    description={activity.description}
                    count={activity.count}
                    change={activity.change}
                    icon={
                      <img src={activity.icon.src} alt={activity.icon.alt} />
                    }
                    dateRange={filter.dateRange}
                    isLoading={isLoading}
                  />
                </Grid>
              ))}
            </Grid>
          </Card>
          <br />
          <Grid container spacing={2} className={styles.chartSection}>
            <Grid item xs={12} sm={6} md={6} display="flex">
              <Card
                cardTitle="Reviewer evaluation status"
                cardSubTitle={
                  filter.dateRange === "thisMonth"
                    ? getReviewerEvaluationTitle(
                        dashboardData?.prevCorrectiveActionStatus?.qcFail,
                        dashboardData?.correctiveActionStatus?.qcFail
                      )
                    : ""
                }
              >
                {isLoading ? (
                  <Skeleton
                    data-testid="skeleton"
                    variant="rounded"
                    width="100%"
                    height={360}
                  />
                ) : (
                  <PieChartContainer
                    data={[
                      {
                        name: "QC Pass",
                        value: dashboardData?.correctiveActionStatus?.qcPass,
                      },
                      {
                        name: "QC Fail",
                        value: dashboardData?.correctiveActionStatus?.qcFail,
                      },
                    ]}
                  />
                )}
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6} display="flex">
              <Card cardTitle="Reviews over time">
                {isLoading ? (
                  <Skeleton
                    data-testid="skeleton"
                    variant="rounded"
                    width="100%"
                    height={360}
                  />
                ) : dashboardData?.reviewsCount?.length === 0 ? (
                  <NoData />
                ) : (
                  <LineChartContainer data={dashboardData?.reviewsCount} />
                )}
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6} display="flex">
              <Card
                cardTitle="Records of Active Users over time"
                cardSubTitle={
                  filter.dateRange === "thisMonth"
                    ? getActiveUserTitle(
                        dashboardData?.prevActiveUser,
                        dashboardData?.activeUsers
                      )
                    : ""
                }
              >
                {isLoading ? (
                  <Skeleton
                    data-testid="skeleton"
                    variant="rounded"
                    width="100%"
                    height={360}
                  />
                ) : dashboardData?.activeUsers?.length === 0 ? (
                  `No Data to display. ${
                    filter.reviewType.length === 0
                      ? "Please review types to populate this widget"
                      : ""
                  }`
                ) : (
                  <BarChartContainer
                    data={dashboardData?.activeUsers}
                    legendConfig={[
                      {
                        key: "viewOnlyUsers",
                        label: "View only User",
                        color: "#10225B",
                      },
                      {
                        key: "responsibleUsers",
                        label: "Responsible",
                        color: "#1C359A",
                      },

                      {
                        key: "adminUsers",
                        label: "Admin",
                        color: "#5081E6",
                      },

                      {
                        key: "reviewerUsers",
                        label: "Reviewer",
                        color: "#455EC3",
                      },
                    ]}
                  />
                )}
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={6} display="flex">
              <Card cardTitle="Notifcation">
                {isLoading ? (
                  <Skeleton
                    data-testid="skeleton"
                    variant="rounded"
                    width="100%"
                    height={360}
                  />
                ) : !dashboardData?.notification ? (
                  <NoData />
                ) : (
                  <BarChartContainer
                    height={400}
                    data={dashboardData?.notification}
                    legendConfig={[
                      {
                        key: "acknowledged",
                        label: "Notification Acknowledged",
                        color: "#1C359A",
                      },
                      {
                        key: "sent",
                        label: "Notification Sent",
                        color: "#6E81D0",
                      },
                    ]}
                  />
                )}
              </Card>
            </Grid>

            <Grid item xs={12} sm={12} md={12} display="flex">
              <Card cardTitle="AI flagged risk vs human risk">
                {isLoading ? (
                  <Skeleton variant="rounded" width="100%" height={360} />
                ) : !dashboardData?.flaggedRisk ? (
                  <NoData />
                ) : (
                  <HorizontalBarChartContainer
                    height={400}
                    data={dashboardData?.flaggedRisk}
                    legendConfig={[
                      {
                        key: "aiRisk",
                        label: "AI Flagged Risk",
                        color: "#1C359A",
                      },
                      {
                        key: "humanRisk",
                        label: "Human Confirmed Risk",
                        color: "#6E81D0",
                      },
                      {
                        key: "humanPassCount",
                        label: "Human Refuted Risk",
                        color: "#10225B",
                      },
                      {
                        key: "pendingAICount",
                        label: "Pending AI Flagged Risk",
                        color: "#5081E6",
                      },
                    ]}
                  />
                )}
              </Card>
            </Grid>

          </Grid>
          <Card
            cardTitle="Review outcomes by Countries"
            cardSubTitle={getReviewOutcomeTitle()}
            infoText="Country based evaluation count (Top 10 countries)"
          >
            {isLoading ? (
              <Skeleton
                data-testid="skeleton"
                variant="rounded"
                width="100%"
                height={360}
              />
            ) : dashboardData?.reviewOutcome?.length === 0 ? (
              <NoData />
            ) : (
              <TreeMap data={dashboardData.reviewOutcome} />
            )}
          </Card>
          <br />
        </Box>

        <Box className={styles.buttonGroup} mr={2}>
          {userInfo?.role?.includes("admin") && (
            <Button
              variant="outlined"
              onClick={handleSystemPerfDB}
              startIcon={<img src={DashboardReportIcon} alt="report icon" />}
            >
              System Performance Dashboard
            </Button>
          )}
          <Button variant="contained" onClick={openExportModal} disabled={!hasData || isLoading || isReadOnly}>
            Export report
          </Button>
        </Box>
        <Suspense fallback={<>Loading...</>}>
          <ExportDashboard
            open={exportModal}
            closeModal={closeExportModal}
            download={handleDownload}
          />
        </Suspense>
      </Container>
      <Footer isFixed={false} />
    </Box>
  );
};

export default Dashboard;
